import {
  percentChangeValueGetter,
  historicalBalancesAgg,
  priceChangePercentAggFunc,
  performanceHeaderTemplate,
  moneyFormatter,
  ownerTypeFormatter,
  percentFormatter,
  primaryOwnerFormatter,
  productTypeFormatter,
  quantityFormatter,
  retirementAccountFormatter,
  priceFormatter,
  personalInvestmentFormatter,
  prrPercentFormatter,
  collegeSavingsPlanFormatter,
  alaska529PlanFormatter,
  balanceValueAggr,
  priceChangeDollarAgg
} from 'services/TableUtils';

const GRID_CELL_CLASSNAME = 'ag-cell';

const getAllFocusableElementsOf = (el) => {
  return Array.from(
    el.querySelectorAll(
      'button, a, [tabindex]:not([tabindex="-1"])'
    )
  );
};

const getEventPath = (event) => {
  const path = [];
  let currentTarget = event.target;
  while (currentTarget) {
    path.push(currentTarget);
    currentTarget = currentTarget.parentElement;
  }
  return path;
};

const suppressKeyboardEvent = ({ event }) => {
  const { key, shiftKey } = event;
  const path = getEventPath(event);
  const isTabForward = key === 'Tab' && shiftKey === false;
  const isTabBackward = key === 'Tab' && shiftKey === true;
  let suppressEvent = false;
  // Handle cell children tabbing
  if (isTabForward || isTabBackward) {
    const eGridCell = path.find((el) => {
      if (el.classList === undefined) return false;
      return el.classList.contains(GRID_CELL_CLASSNAME);
    });
    if (!eGridCell) {
      return suppressEvent;
    }
    const focusableChildrenElements = getAllFocusableElementsOf(eGridCell);
    const lastCellChildEl =
      focusableChildrenElements[focusableChildrenElements.length - 1];
    const firstCellChildEl = focusableChildrenElements[0];
    // Suppress keyboard event if tabbing forward within the cell and the current focused element is not the last child
    if (isTabForward && focusableChildrenElements.length > 0) {
      const isLastChildFocused =
        lastCellChildEl && document.activeElement === lastCellChildEl;
      if (!isLastChildFocused) {
        suppressEvent = true;
      }
    }
    // Suppress keyboard event if tabbing backwards within the cell, and the current focused element is not the first child
    else if (isTabBackward && focusableChildrenElements.length > 0) {
      const cellHasFocusedChildren =
        eGridCell.contains(document.activeElement) &&
        eGridCell !== document.activeElement;
      // Manually set focus to the last child element if cell doesn't have focused children
      if (!cellHasFocusedChildren) {
        lastCellChildEl.focus();
        // Cancel keyboard press, so that it doesn't focus on the last child and then pass through the keyboard press to
        // move to the 2nd last child element
        event.preventDefault();
      }
      const isFirstChildFocused =
        firstCellChildEl && document.activeElement === firstCellChildEl;
      if (!isFirstChildFocused) {
        suppressEvent = true;
      }
    }
  }
  return suppressEvent;
};

/*
 https://www.ag-grid.com/javascript-grid-column-properties/
 sortable: allows sorting on each column
 suppressMenu: suppresses built-in filtering/selection menu
 animateRows: animates rows upon data, sort, or filter change
 aggFunc: column aggregates default to sum of the column
 minWidth: minimum width for each column
 */

export const defaultColumnDef = {
  sortable: true,
  suppressMenu: true,
  animateRows: true,
  suppressMovable: true,
  aggFunc: 'sum',
  autoHeight: true,
  suppressSizeToFit: false,
  resizable: true,
  suppressKeyboardEvent,
};

/*
 https://www.ag-grid.com/javascript-grid-column-properties/
 headerName: empty string removes name from column header
 field: value upon which the group column aggregates data (in this case, the account's name and checksum)
 pinned: pins column to left for constant viewing
 wrapText: wraps column text to prevent column from growing from a single line of text
 autoHeight: automatically sets minimum row height based on this column's data
 min/max/width: sets width of column
 cellClass: sets css class applied to this particular column
 tooltipField: sets data to be displayed in hoverable tooltip
 sort: default sort for column (descending alphabetical in this case)
 rowGroupIndex: sets which layer of group this column corresponds to (in this case, 4 is the lowest grouping.)
 cellRendererParams: parameters sent to cell renderer, modifying what is displayed in the column's cells
 suppressCount: suppresses display of a count of how many children belong to each group
 suppressPadding: suppresses automatic lefthand padding on each level of group (i.e. without this lower groups would have
 crazy indents)
 innerRenderer: sets cell renderer to be used
 */

export const autoGroupColumnDef = {
  headerName: 'Account',
  field: 'accountNameAndNumber',
  pinned: 'left',
  wrapText: true,
  autoHeight: true,
  minWidth: 365,
  width: 365,
  maxWidth: 365, // bug in ag-grid where even if a width in defined it will resize beyond width on resize event
  suppressSizeToFit: true,
  cellClass: 'pinned-cell-align lock-pinned',
  sort: 'asc',
  rowGroupIndex: 4,
  resizable: false,
  cellRendererParams: {
    suppressCount: true,
    suppressDoubleClickExpand: true,
    suppressEnterExpand: true,
    suppressPadding: true,
    innerRenderer: 'accountNameRenderer'
  }
};

export const autoGroupColumnDefMobile = {
  headerName: 'Account',
  field: 'accountNameAndNumber',
  pinned: 'left',
  wrapText: true,
  autoHeight: true,
  minWidth: 230,
  width: 230,
  maxWidth: 230, // bug in ag-grid where even if a width in defined it will resize beyond width on resize event
  suppressSizeToFit: true,
  resizable: false,
  cellClass: 'pinned-cell-align lock-pinned',
  sort: 'asc',
  rowGroupIndex: 4,
  cellRendererParams: {
    suppressPadding: true,
    innerRenderer: 'accountNameRenderer'
  }
};

export function createColumnDef(
  retirement = false,
  has529Only = false,
  hasAlaska529Only = false,
  key,
  historicalBalances
) {
  const { selectedDate } = historicalBalances;

  let def;

  if (isRowGroup(key)) {
    def = new RowGroupCol(key, retirement, has529Only, hasAlaska529Only);
  } else if (isColHidden(key)) {
    def = new HiddenCol();
  } else if (key === 'balance') {
    def = new BalanceCol(historicalBalances);
  } else if (key === 'performance') {
    def = new PerformanceCol(selectedDate);
  } else if (key === 'priceChangePercentSum') {
    def = new PriceChangePercentCol(selectedDate);
  } else if (key === 'quantitySum') {
    def = new QuantityCol(selectedDate);
  } else if (key === 'difference') {
    def = new DailyChangeCol(selectedDate);
  } else if (key === 'priceSum') {
    def = new PriceCol(selectedDate);
  } else if (key === 'dailyPriceChangeSum') {
    def = new DailyPriceChangeCol(selectedDate);
  } else {
    def = new RegCol();
  }

  def.field = key;
  return def;
}

const rowGroups = [
  'personalInvestment',
  'retirementType',
  'retirementAccount',
  'primaryOwner',
  'productType',
  'isOwner',
  'groupId'
];

export const isRowGroup = (key) => {
  return rowGroups.includes(key);
};

// keys in data that do not need to be individual columns
const hiddenData = [
  'accountName',
  'accountId',
  'accountNumber',
  'accountType',
  'balanceValue',
  'groupId',
  'cash',
  'isViewOnly',
  'isMoneyMarket',
  'owners',
  'performanceInceptionDate',
  'isActivePlusOrBrokerage',
  'previousBalanceValue',
  'sweepAccount',
  'sweepAccountId',
  'ticker',
  'mmrMessage',
  'activeShareClassConverted'
];

// keys used for filtering
const filteringData = ['isRelated', 'owner', 'hiddenAccount'];

// keys used in rendering
export const renderingData = [
  'accountDescriptor',
  'accountNumberWChecksum',
  'accountNameAndNumber',
  'inceptionDate',
  'balanceAsOfDate'
];

export const hiddenColumns = [
  ...hiddenData,
  ...filteringData,
  ...renderingData
];

const hideColumn = (key) => {
  return hiddenColumns.includes(key);
};

const isColHidden = (key) => hideColumn(key);

export const visibleColumns = [
  'quantitySum',
  'priceSum',
  'dailyPriceChangeSum',
  'balance',
  'difference',
  'priceChangePercentSum',
  'performance'
];

export const orderedColumnKeys = [
  ...rowGroups,
  ...visibleColumns,
  ...hiddenColumns
];

const orderedRowGroups = [
  'personalInvestment',
  'productType',
  'retirementAccount',
  'isOwner',
  'primaryOwner',
  'groupId'
];
const orderedRetirementRowGroups = [
  'retirementAccount',
  'productType',
  'isOwner',
  'primaryOwner',
  'groupId'
];

const RowGroupCol = function (key, retirement, has529Only, hasAlaska529Only) {
  this.headerName = '';
  this.rowGroup = true;
  this.hide = true;
  this.cellClass = 'pinned-cell-align';
  this.rowGroupIndex = retirement
    ? orderedRetirementRowGroups.indexOf(key)
    : orderedRowGroups.indexOf(key);

  if (key === 'personalInvestment') {
    this.valueFormatter = has529Only
      ? hasAlaska529Only
        ? alaska529PlanFormatter
        : collegeSavingsPlanFormatter
      : personalInvestmentFormatter;
  }

  if (key === 'primaryOwner') {
    this.valueFormatter = primaryOwnerFormatter;
  }

  if (key === 'productType') {
    this.valueFormatter = productTypeFormatter;
  }

  if (key === 'retirementAccount') {
    this.valueFormatter = retirementAccountFormatter;
  }

  if (key === 'isOwner') {
    this.valueFormatter = ownerTypeFormatter;
  }
};

const QuantityCol = function () {
  this.headerName = 'Quantity';
  this.valueFormatter = quantityFormatter;
  this.type = 'rightAligned';
  this.minWidth = 120;
};

const PriceCol = function () {
  this.headerName = 'Price';
  this.valueFormatter = priceFormatter;
  this.type = 'rightAligned';
  this.minWidth = 70;
};

const DailyPriceChangeCol = function () {
  this.headerName = 'Daily Price Change';
  this.valueFormatter = priceFormatter;
  this.cellRenderer = 'priceWithIndicator';
  this.type = 'rightAligned';
  this.minWidth = 75;
};

const BalanceCol = function (historicalBalances) {
  if (historicalBalances && historicalBalances.selectedDate) {
    this.aggFunc = historicalBalancesAgg(historicalBalances);
  } else {
    this.aggFunc = balanceValueAggr;
  }
  this.valueFormatter = moneyFormatter;
  this.type = 'rightAligned';
  this.minwidth = 120;
};

const DailyChangeCol = function () {
  this.headerName = 'Daily Change ($)';
  this.valueFormatter = moneyFormatter;
  this.aggFunc = priceChangeDollarAgg;
  this.cellRenderer = 'dollarWithIndicator';
  this.type = 'rightAligned';
  this.minWidth = 160;
};

const PriceChangePercentCol = function () {
  this.headerName = 'Daily Change (%)';
  this.valueFormatter = percentFormatter;
  this.valueGetter = percentChangeValueGetter;
  this.aggFunc = priceChangePercentAggFunc;
  this.minWidth = 80;
  this.type = 'rightAligned';
  this.cellRenderer = 'percentWithIndicator';
};

const PerformanceCol = function () {
  this.valueFormatter = prrPercentFormatter(false);
  this.headerComponentParams = { template: performanceHeaderTemplate };
  this.aggFunc = 'avg';
  this.cellRenderer = 'prrRenderer';
  this.type = 'rightAligned';
  this.minWidth = 95;
};

const RegCol = function () {
  this.valueFormatter = moneyFormatter;
};

const HiddenCol = function () {
  this.hide = true;
};
